import React, {type ReactNode} from 'react';

import {emptyObject} from '@packages/utilities/fp/utils';

import {useFormGroupState} from '../FormGroup';
import {getDefinedValue} from '../../utils';
import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';

import {FormLabelView, RequiredIndicatorView} from './FormLabel.styles';

export type FormLabelProps = {
  /**
   * The content for FormLabel.
   */
  children?: ReactNode;
  /**
   * A manual override for the `for` attribute of the FormLabel element.
   */
  htmlFor?: string;
  /**
   * A way to indicate that the FormLabel is displayed next to its input in order to use built-in alignment.
   */
  isHorizontal?: boolean;
  /**
   * A way to visually indicate that the input associated with the FormLabel is required.
   */
  isRequired?: boolean;
  /**
   * This prop is used to hide the required asterisk.
   * We do not want consumers using this escape hatch so much, but we need it for Radios and Checkboxes.
   */
  _shouldHideRequired?: boolean;
} & HTMLAttributes &
  SpringTooling;

/**
 * # FormLabel
 *
 * A label for form inputs. The recommended way to use `FormLabel` is actually to not use it directly. When you use `FormGroup` and pass it the `label` prop, a `FormLabel` component gets created for you.
 *
 * ## FormGroup Usage
 * ```jsx
 * import {FormGroup, TextInput} from '@packages/systems/spring';
 *
 * const Example = () => <FormGroup label="Label text"><TextInput /></FormGroup>
 * ```
 *
 * However, if you wish to use `FormLabel` outside of a `FormGroup`, you can import it and use it directly.
 *
 * ## Standalone Usage
 * ```jsx
 * import {FormLabel, TextInput, VStack} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   return (
 *     <VStack>
 *       <FormLabel htmlFor="my-input">Label text</FormLabel>
 *       <TextInput id="my-input" />
 *     </VStack>
 *   );
 * };
 * ```
 *
 * @component
 */
export const FormLabel = React.forwardRef<HTMLLabelElement, FormLabelProps>(
  (
    {
      children,
      htmlFor: htmlForProp,
      id: idProp,
      _shouldHideRequired,
      isHorizontal: isHorzontalProp,
      isRequired: isRequiredProp,
      style = emptyObject,
      ...props
    }: FormLabelProps,
    ref
  ) => {
    const {
      htmlFor,
      labelId,
      labelWidth,
      isHorizontal: isHorizontalContext,
      isRequired: isRequiredStateFromFormGroup,
    } = useFormGroupState();

    const id = getDefinedValue(idProp, labelId);
    const isHorizontal = getDefinedValue(isHorzontalProp, isHorizontalContext);
    const isRequired = getDefinedValue(
      isRequiredStateFromFormGroup,
      isRequiredProp
    );

    return (
      <FormLabelView
        alignToInput={isHorizontal ? 'horizontal' : undefined}
        htmlFor={htmlForProp || htmlFor}
        {...props}
        id={id}
        ref={ref}
        style={{
          width: isHorizontal ? labelWidth : undefined,
          ...style,
        }}
      >
        {children}
        {isRequired && !_shouldHideRequired ? (
          <RequiredIndicatorView>*</RequiredIndicatorView>
        ) : null}
      </FormLabelView>
    );
  }
);

FormLabel.displayName = 'FormLabel';
FormLabel.toString = FormLabelView.toString;

export default FormLabel;
